import z from "zod";

import { type ErrorDetails, RemiHttpCode } from "./remi-app";

const remiNextjsErrorTypeValues = [
  "ACTIVE_STRUCTURE_MISSING",
  "CLIPBOARD_COPY_FAILED",
  "COMPONENT_ROW_MISSING",
  "DANGEROUS_FILE_TYPE_NEUTRALIZED",
  "FILE_ADD_FAILED",
  "INVALID_WEBHOOK_BODY",
  "MAPBOX_DIRECTIONS_REQUEST_FAILED",
  "MCP_CONTEXT_NOT_STASHED",
  "MISSING_HOMEOWNER_QUOTE_LINK",
  "PROVIDER_CONTEXT_MISSING",
  "RECORD_ATTEMPT_ACTOR_NOT_FOUND",
  "SELECTION_KIND_MISMATCH",
  "TRPC_NON_JSON_RESPONSE",
] as const;

const remiNextjsErrorTypeSchema = z.enum(remiNextjsErrorTypeValues);
export const RemiNextjsErrorType = remiNextjsErrorTypeSchema.enum;
export type RemiNextjsErrorType = z.infer<typeof remiNextjsErrorTypeSchema>;

export type RemiNextjsErrors =
  | {
      errorType: typeof RemiNextjsErrorType.ACTIVE_STRUCTURE_MISSING;
      data: { structureId: string };
    }
  | {
      errorType: typeof RemiNextjsErrorType.CLIPBOARD_COPY_FAILED;
      data: Record<string, never>;
    }
  | {
      errorType: typeof RemiNextjsErrorType.COMPONENT_ROW_MISSING;
      data: { structureId: string; sourceId: string };
    }
  | {
      errorType: typeof RemiNextjsErrorType.DANGEROUS_FILE_TYPE_NEUTRALIZED;
      data: { fileId: string; detectedMime: string | undefined; detectionMethod: string };
    }
  | {
      errorType: typeof RemiNextjsErrorType.FILE_ADD_FAILED;
      data: { fileName: string; fileSize: number; rejectedBy: "getFileType" | "getUploadFile" };
    }
  | {
      errorType: typeof RemiNextjsErrorType.INVALID_WEBHOOK_BODY;
      data: {
        receivedBody: unknown;
        headers: Record<string, string | string[] | undefined>;
        path: string;
      };
    }
  | {
      errorType: typeof RemiNextjsErrorType.MAPBOX_DIRECTIONS_REQUEST_FAILED;
      data: { status: number };
    }
  | {
      errorType: typeof RemiNextjsErrorType.MCP_CONTEXT_NOT_STASHED;
      data: Record<string, never>;
    }
  | {
      errorType: typeof RemiNextjsErrorType.MISSING_HOMEOWNER_QUOTE_LINK;
      data: {
        mutation: string;
        id: string;
      };
    }
  | {
      errorType: typeof RemiNextjsErrorType.PROVIDER_CONTEXT_MISSING;
      data: { hook: string; provider: string };
    }
  | {
      errorType: typeof RemiNextjsErrorType.RECORD_ATTEMPT_ACTOR_NOT_FOUND;
      data: { personId: string };
    }
  | {
      errorType: typeof RemiNextjsErrorType.SELECTION_KIND_MISMATCH;
      data: { hook: string; expectedKind: string; selectedKind: string | null };
    }
  | {
      errorType: typeof RemiNextjsErrorType.TRPC_NON_JSON_RESPONSE;
      data: {
        path: string;
        status: number;
        contentType: string | null;
        vercelError: string | null;
        requestId: string | null;
      };
    };

export const remiNextjsErrorDetails: Record<RemiNextjsErrorType, ErrorDetails> = {
  [RemiNextjsErrorType.ACTIVE_STRUCTURE_MISSING]: {
    sentryDescription: "The property-model state names an active structure it does not hold",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.CLIPBOARD_COPY_FAILED]: {
    sentryDescription: "Clipboard copy failed on both the modern API and the execCommand fallback",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.COMPONENT_ROW_MISSING]: {
    sentryDescription: "A geometry command removed a component the property-model tree does not hold",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.DANGEROUS_FILE_TYPE_NEUTRALIZED]: {
    sentryDescription: "Dangerous file type detected post-upload and neutralized to application/octet-stream",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.FILE_ADD_FAILED]: {
    sentryDescription: "A selected file was rejected before upload and never reached the uploader",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.INVALID_WEBHOOK_BODY]: {
    sentryDescription: "Invalid webhook body",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.MAPBOX_DIRECTIONS_REQUEST_FAILED]: {
    sentryDescription: "Mapbox Directions returned a non-OK response for a crew route",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.MCP_CONTEXT_NOT_STASHED]: {
    sentryDescription: "MCP auth succeeded but context was not stashed on the request",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.MISSING_HOMEOWNER_QUOTE_LINK]: {
    sentryDescription: "Homeowner quote link mutation returned no link",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.PROVIDER_CONTEXT_MISSING]: {
    sentryDescription: "A context hook was called outside its provider",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.RECORD_ATTEMPT_ACTOR_NOT_FOUND]: {
    sentryDescription: "Record-attempt workflow could not resolve the acting person",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.SELECTION_KIND_MISMATCH]: {
    sentryDescription: "A selection-kind hook rendered while a different kind was selected",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [RemiNextjsErrorType.TRPC_NON_JSON_RESPONSE]: {
    sentryDescription: "The server was temporarily unavailable. Please try again.",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
};
